import React from "react";
import { BarChart3, X } from "lucide-react";
import {
  appendDashboardEmailItems,
  DASHBOARD_EMAIL_PRESETS,
  dashboardEmailSettingsFromItems,
  dashboardEmailItemsToAppend,
  REPORT_EMAIL_MAX_ITEMS,
} from "../utils/reportEmailForm.js";

const PICKER_BUTTON_BASE = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-bold focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50";
const PICKER_BUTTON = `${PICKER_BUTTON_BASE} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
const PICKER_PRIMARY_BUTTON = `${PICKER_BUTTON_BASE} border border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`;
const PICKER_FIELD = "min-h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20";
const LABEL = "grid min-w-0 content-start gap-1 text-xs font-semibold text-slate-700";

function dashboardOptions(catalog) {
  return Array.isArray(catalog?.dashboards) ? catalog.dashboards.filter((option) => option?.key) : [];
}

export default function ReportEmailDashboardPicker({
  actionItems = [],
  catalog = null,
  catalogLoading = false,
  catalogError = "",
  onAdd,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedKeys, setSelectedKeys] = React.useState([]);
  const [settings, setSettings] = React.useState(() => dashboardEmailSettingsFromItems(actionItems));
  const [draftError, setDraftError] = React.useState("");
  const pickerId = React.useId();
  const titleId = React.useId();
  const options = dashboardOptions(catalog);
  const selected = new Set(selectedKeys);
  const capacity = Math.max(0, REPORT_EMAIL_MAX_ITEMS - actionItems.length);
  const pendingItems = dashboardEmailItemsToAppend(actionItems, selectedKeys, settings, options);
  const pendingCount = pendingItems.length;
  const exceedsCapacity = pendingCount > capacity;
  const fixedDateMissing = settings.date_mode === "fixed" && !settings.fixed_date;
  const catalogReady = Array.isArray(catalog?.dashboards) && !catalogError;

  function openPicker() {
    setSelectedKeys([]);
    setSettings(dashboardEmailSettingsFromItems(actionItems));
    setDraftError("");
    setOpen(true);
  }

  function toggleKey(key) {
    setDraftError("");
    setSelectedKeys((current) => current.includes(key) ? current.filter((value) => value !== key) : [...current, key]);
  }

  function updateSettings(patch) {
    setDraftError("");
    setSettings((current) => ({
      ...current,
      ...patch,
      ...(patch.date_mode && patch.date_mode !== "fixed" ? { fixed_date: null } : {}),
    }));
  }

  function applySelection() {
    if (!selectedKeys.length) {
      setDraftError("Sélectionnez au moins un graphique.");
      return;
    }
    if (exceedsCapacity) {
      setDraftError(`Réduisez la sélection à ${capacity} ajout(s) maximum.`);
      return;
    }
    if (fixedDateMissing) {
      setDraftError("Choisissez la date de chaque graphique à date fixe.");
      return;
    }
    onAdd(appendDashboardEmailItems(actionItems, selectedKeys, settings, options));
    setOpen(false);
  }

  return (
    <div className={`grid min-w-0 gap-1.5 ${open ? "w-full" : ""}`}>
      <button
        type="button"
        className={PICKER_BUTTON}
        disabled={disabled || catalogLoading || !!catalogError || !catalogReady || capacity <= 0}
        aria-controls={open ? pickerId : undefined}
        aria-expanded={open}
        onClick={openPicker}
      >
        <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" /> Ajouter des graphiques
      </button>
      {catalogLoading ? <p role="status" className="m-0 text-xs text-slate-500">Chargement du catalogue des graphiques…</p> : null}
      {!catalogLoading && catalogError ? <p role="alert" className="m-0 rounded-[4px] border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">{catalogError}</p> : null}
      {open ? (
        <section id={pickerId} role="region" aria-labelledby={titleId} className="grid min-w-0 w-full gap-3 rounded-[4px] border border-slate-200 bg-white p-3 shadow-[0_14px_30px_rgba(15,23,42,0.12)]">
          <header className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 id={titleId} className="m-0 text-sm font-bold text-slate-900">Ajouter des graphiques</h3>
              <p className="m-0 mt-1 text-xs leading-5 text-slate-500">Les graphiques cochés seront ajoutés dans l’ordre du catalogue. {capacity} place(s) restante(s).</p>
            </div>
            <button type="button" className={PICKER_BUTTON} aria-label="Fermer la liste des graphiques" onClick={() => setOpen(false)}><X className="h-3.5 w-3.5" aria-hidden="true" /></button>
          </header>
          {options.length ? <>
            <div className="grid min-w-0 gap-1.5">
              <p className="m-0 text-xs font-bold text-slate-700">Présélections</p>
              <div className="flex flex-wrap gap-1.5">
                {DASHBOARD_EMAIL_PRESETS.map((preset) => {
                  const isSelected = selectedKeys.length === preset.dashboardKeys.length && preset.dashboardKeys.every((key) => selected.has(key));
                  return <button key={preset.key} type="button" aria-pressed={isSelected} className={isSelected ? PICKER_PRIMARY_BUTTON : PICKER_BUTTON} onClick={() => { setSelectedKeys(preset.dashboardKeys.filter((key) => options.some((option) => option.key === key))); setDraftError(""); }}>{preset.label}</button>;
                })}
              </div>
            </div>
            <fieldset className="m-0 grid min-w-0 gap-1 border-0 p-0">
              <legend className="text-xs font-bold text-slate-700">Graphiques</legend>
              <div className="grid min-w-0 gap-1 sm:grid-cols-2">
                {options.map((option) => {
                  const checked = selected.has(option.key);
                  return <label key={option.key} className="flex min-w-0 items-start gap-2 rounded-[4px] px-1.5 py-1 text-xs text-slate-700"><input type="checkbox" className="mt-0.5 accent-accent" checked={checked} onChange={() => toggleKey(option.key)} /><span className="min-w-0 break-words">{option.label}</span></label>;
                })}
              </div>
            </fieldset>
            <fieldset className="m-0 grid min-w-0 gap-2 rounded-[4px] border border-slate-200 p-2.5">
              <legend className="px-1 text-xs font-bold text-slate-700">Paramètres communs</legend>
              <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <label className={LABEL}>Date des données<select aria-label="Date des données communes" className={PICKER_FIELD} value={settings.date_mode} onChange={(event) => updateSettings({ date_mode: event.target.value })}><option value="today">Aujourd’hui</option><option value="yesterday">Hier</option><option value="tomorrow">Demain</option><option value="fixed">Date fixe</option></select></label>
                {settings.date_mode === "fixed" ? <label className={LABEL}>Date fixe<input type="date" aria-label="Date fixe commune" className={PICKER_FIELD} value={settings.fixed_date || ""} onChange={(event) => updateSettings({ fixed_date: event.target.value || null })} /></label> : null}
                <label className={LABEL}>Dispatch<select aria-label="Dispatch commun" className={PICKER_FIELD} value={settings.dispatch_period} onChange={(event) => updateSettings({ dispatch_period: event.target.value })}><option value="day">Jour</option><option value="evening">Soir</option></select></label>
                <label className={LABEL}>Période<select aria-label="Période commune" className={PICKER_FIELD} value={settings.period} onChange={(event) => updateSettings({ period: event.target.value })}><option value="day">Un jour</option><option value="week">7 derniers dispatchs actifs</option><option value="month">30 derniers dispatchs actifs</option></select></label>
              </div>
            </fieldset>
          </> : <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Aucun graphique disponible dans le catalogue.</p>}
          {selectedKeys.length && !pendingCount ? <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Les graphiques sélectionnés sont déjà présents avec ces paramètres.</p> : null}
          {selectedKeys.length && pendingCount ? <p className={`m-0 text-xs ${exceedsCapacity ? "font-semibold text-red-800" : "text-slate-500"}`}>{pendingCount} ajout(s) seront effectué(s) avec ces paramètres.{exceedsCapacity ? ` Réduisez la sélection à ${capacity} ajout(s) maximum.` : ""}</p> : null}
          {fixedDateMissing ? <p role="alert" className="m-0 rounded-[4px] border border-red-200 bg-red-50 p-2 text-xs text-red-800">Choisissez une date fixe avant d’ajouter les graphiques.</p> : null}
          {draftError ? <p role="alert" className="m-0 rounded-[4px] border border-red-200 bg-red-50 p-2 text-xs text-red-800">{draftError}</p> : null}
          <footer className="flex flex-wrap justify-end gap-1.5">
            <button type="button" className={PICKER_BUTTON} onClick={() => setOpen(false)}>Annuler</button>
            <button type="button" className={PICKER_PRIMARY_BUTTON} disabled={!pendingCount || exceedsCapacity || fixedDateMissing || capacity <= 0} onClick={applySelection}>Ajouter {pendingCount ? `(${pendingCount})` : ""}</button>
          </footer>
        </section>
      ) : null}
    </div>
  );
}
